/**
 * DP 链独立钱包加密工具（移植自链端 web/wallet.html）。
 *  - 密钥：WebCrypto Ed25519（PKCS8 前缀 16B + 32B seed）
 *  - 地址：'0x' + sha256(pub).slice(0,40)
 *  - 签名：canonical(tx) 与 dpchain/txcodec.py 逐字节一致 → Ed25519 签名
 *  - 保管：PBKDF2(150000) + AES-GCM 加密后存 localStorage
 */

const PKCS8_PREFIX = new Uint8Array([
  0x30, 0x2e, 0x02, 0x01, 0x00, 0x30, 0x05, 0x06, 0x03, 0x2b, 0x65, 0x70, 0x04, 0x22, 0x04, 0x20,
]) as Uint8Array<ArrayBuffer>;

const enc = new TextEncoder();
const dec = new TextDecoder();

/** seed → Ed25519 公钥（导入钱包时派生地址用；签名仍走 WebCrypto，与链端 wallet.html 一致）。 */
async function pubFromSeed(seedHex: string): Promise<Uint8Array> {
  const { ed25519 } = await import('@noble/curves/ed25519');
  return ed25519.getPublicKey(hex2buf(seedHex));
}

/** 从 seedHex 重建钱包的 pub/address（导入用）。 */
export async function importKeyFromSeed(seedHex: string): Promise<{ pubHex: string; address: string }> {
  const pub = await pubFromSeed(seedHex);
  const pubHex = buf2hex(pub);
  return { pubHex, address: '0x' + (await sha256Hex(pub)).slice(0, 40) };
}

export function buf2hex(buf: ArrayBuffer | Uint8Array): string {
  const u = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  return Array.from(u)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export function hex2buf(hex: string): Uint8Array<ArrayBuffer> {
  const s = hex.replace(/^0x/i, '');
  const out = new Uint8Array(s.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(s.slice(i * 2, i * 2 + 2), 16);
  return out;
}

export function rand(n: number): Uint8Array<ArrayBuffer> {
  const u = new Uint8Array(n);
  crypto.getRandomValues(u);
  return u;
}

function concat(a: Uint8Array, b: Uint8Array): Uint8Array<ArrayBuffer> {
  const c = new Uint8Array(a.length + b.length);
  c.set(a);
  c.set(b, a.length);
  return c;
}

export async function sha256Hex(buf: Uint8Array | ArrayBuffer): Promise<string> {
  const u = new Uint8Array(buf); // 拷贝为确定 ArrayBuffer，规避 SharedArrayBuffer 泛型
  return buf2hex(await crypto.subtle.digest('SHA-256', u.buffer));
}

export async function addressOfPub(pubBuf: ArrayBuffer | Uint8Array<ArrayBuffer>): Promise<string> {
  return '0x' + (await sha256Hex(pubBuf)).slice(0, 40);
}

export interface ChainWallet {
  name: string;
  seedHex: string;
  pubHex: string;
  address: string;
}

/** 创建新钱包：Ed25519 随机密钥 → seed/pub/address。 */
export async function genWallet(name: string): Promise<ChainWallet> {
  const kp = await crypto.subtle.generateKey({ name: 'Ed25519' }, true, ['sign', 'verify']);
  const der = new Uint8Array(await crypto.subtle.exportKey('pkcs8', kp.privateKey));
  const seed = der.slice(16);
  const pub = new Uint8Array(await crypto.subtle.exportKey('raw', kp.publicKey));
  return { name, seedHex: buf2hex(seed), pubHex: buf2hex(pub), address: await addressOfPub(pub) };
}

async function keyFromSeed(seedHex: string): Promise<CryptoKey> {
  const der = concat(PKCS8_PREFIX, hex2buf(seedHex));
  return crypto.subtle.importKey('pkcs8', der, { name: 'Ed25519' }, false, ['sign']);
}

const micro = (x: number): string => {
  const v = Number(x);
  if (!(v > 0) || !Number.isFinite(v)) throw new Error('金额必须为正数');
  return String(Math.round(v * 1e6));
};

/** 与 dpchain/txcodec.py 逐字节一致的规范消息。 */
export function canonical(tx: Record<string, unknown>): string {
  const L = ['DPB/1', 'type:' + tx.type];
  const t = tx.type as string;
  if (t === 'transfer') {
    L.push('from:' + String(tx.from).toLowerCase());
    L.push('to:' + String(tx.to).toLowerCase());
    L.push('amount:' + String(tx.amount));
    if (tx.token && String(tx.token).toLowerCase() !== 'dpb') L.push('token:' + String(tx.token).toLowerCase());
  } else if (t === 'burn') {
    L.push('from:' + String(tx.from).toLowerCase());
    L.push('amount:' + String(tx.amount));
  } else if (t === 'mint_dpx') {
    L.push('from:' + String(tx.from).toLowerCase());
    L.push('dpx:' + micro(Number(tx.dpx)));
    L.push('price:' + micro(Number(tx.price ?? 1)));
    L.push('ratio:' + micro(Number(tx.ratio ?? 1)));
  } else if (t === 'redeem_dpx') {
    L.push('from:' + String(tx.from).toLowerCase());
    L.push('burn:' + micro(Number(tx.burn)));
    L.push('price:' + micro(Number(tx.price ?? 1)));
  } else if (t === 'mine') {
    L.push('from:' + String(tx.from).toLowerCase());
    if (tx.challenge) L.push('challenge:' + String(tx.challenge).toLowerCase());
    if (tx.nonce) L.push('nonce:' + String(tx.nonce).toLowerCase());
  }
  if (tx.nonce) L.push('nonce:' + String(tx.nonce).toLowerCase());
  return L.join('\n');
}

/** 签名交易：补 pubkey + sig，供 POST /api/transactions 提交。 */
export async function signTx(
  w: Pick<ChainWallet, 'seedHex' | 'pubHex'>,
  tx: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const priv = await keyFromSeed(w.seedHex);
  const sig = await crypto.subtle.sign({ name: 'Ed25519' }, priv, enc.encode(canonical(tx)));
  return { ...tx, pubkey: w.pubHex, sig: buf2hex(sig) };
}

/* ---------------- 口令加密密钥库（PBKDF2 + AES-GCM） ---------------- */

const VAULT_KEY = 'dpchain_wallet_vault';

async function deriveVaultKey(password: string, saltHex: string, usages: KeyUsage[]): Promise<CryptoKey> {
  const base = await crypto.subtle.importKey('raw', enc.encode(password), 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: hex2buf(saltHex), iterations: 150000, hash: 'SHA-256' },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    usages,
  );
}

export async function saveVault(password: string, wallets: ChainWallet[]): Promise<void> {
  const salt = rand(16);
  const iv = rand(12);
  const key = await deriveVaultKey(password, buf2hex(salt), ['encrypt']);
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, enc.encode(JSON.stringify(wallets)));
  window.localStorage.setItem(
    VAULT_KEY,
    JSON.stringify({ salt: buf2hex(salt), iv: buf2hex(iv), data: buf2hex(ct) }),
  );
}

export async function openVault(password: string): Promise<ChainWallet[]> {
  const raw = window.localStorage.getItem(VAULT_KEY);
  if (!raw) return [];
  const v = JSON.parse(raw) as { salt: string; iv: string; data: string };
  const key = await deriveVaultKey(password, v.salt, ['decrypt']);
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: hex2buf(v.iv) }, key, hex2buf(v.data));
  return JSON.parse(dec.decode(pt)) as ChainWallet[];
}

export function hasVault(): boolean {
  return window.localStorage.getItem(VAULT_KEY) !== null;
}
